import { useCallback, useRef, useState } from 'react'
import { Archive, ArchiveRestore, Copy, MoreHorizontal, ThumbsDown, ThumbsUp, Undo2 } from 'lucide-react'
import { Menu } from '@highlight/ui'
import { MessageFeedbackPopover } from '@renderer/components/feedback'
import { feedbackSubject } from '@renderer/chat/api/feedback'
import { copyTextToClipboard } from '@renderer/utils/clipboard'
import { trackStagedActionCopy } from '../../../hooks/staged-actions/track-staged-action'
import type { StagedAction } from '../../../hooks/use-staged-actions'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import { CopyBraintrustLinkMenuItem } from '../../../components/copy-braintrust-link-menu-item'
import { useActionArchiveControl } from './action-utilities'
import { resolveActionSpanLink } from '../../../components/braintrust-links'

interface ActionViewOverflowMenuProps {
  action: StagedAction
  braintrustLink?: string
  /** The action's content, when there is something to put on the clipboard. */
  copyContent?: string
  /** Undo, once the action has been sent and its integration can reverse it. */
  undo?: { label: string; disabled: boolean; onSelect: () => void }
}

/**
 * Every action control except the primary CTA, folded into one circle (Figma
 * 8004:32399). Mounted in place of the control row's utility cluster while a
 * meeting holds the left of the floating bar — 384px doesn't fit both, and the
 * CTA is the one control that has to stay legible.
 *
 * The keyboard doesn't collapse with the buttons: ⌘↵ and ⌘Z stay bound in the
 * control row, which derives them from the action's state rather than from
 * what's on screen.
 */
export function ActionViewOverflowMenu({ action, braintrustLink, copyContent, undo }: ActionViewOverflowMenuProps) {
  // Anchors the rating popover to the circle the menu opened from. A wrapper
  // rather than a ref on the trigger: `Menu.Trigger` is a plain function
  // component, so a ref handed to it would go nowhere on React 18.
  const anchorRef = useRef<HTMLDivElement>(null)
  const archive = useActionArchiveControl(action)
  const spanLink = resolveActionSpanLink(action, braintrustLink)

  const feedback = feedbackSubject(action)
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null)

  const handleCopy = useCallback(async () => {
    if (!copyContent) {
      return
    }
    if (!(await copyTextToClipboard(copyContent))) {
      return
    }
    trackStagedActionCopy(action, 'content', { source: 'action-view' })
  }, [copyContent, action])

  return (
    <div ref={anchorRef} className="flex shrink-0 items-center">
      <Menu>
        <Menu.Trigger variant="outline" shape="square" aria-label="More options" className={FLOATING_FILL_CLASS}>
          <MoreHorizontal aria-hidden="true" />
        </Menu.Trigger>
        <Menu.Content side="top" align="end" className="w-48">
          {feedback && (
            <>
              <Menu.Item onClick={() => setFeedbackType('positive')}>
                <ThumbsUp />
                <span>Helpful</span>
              </Menu.Item>
              <Menu.Item onClick={() => setFeedbackType('negative')}>
                <ThumbsDown />
                <span>Not helpful</span>
              </Menu.Item>
            </>
          )}
          {copyContent && (
            <Menu.Item onClick={handleCopy}>
              <Copy />
              <span>Copy content</span>
            </Menu.Item>
          )}
          {undo && (
            <Menu.Item disabled={undo.disabled} onClick={undo.onSelect}>
              <Undo2 />
              <span>{undo.label}</span>
            </Menu.Item>
          )}
          <Menu.Item onClick={archive.onSelect}>
            {archive.isArchived ? <ArchiveRestore /> : <Archive />}
            <span>{archive.label}</span>
          </Menu.Item>
          {/* Dev-only affordance, last so it never sits between two real controls. */}
          <CopyBraintrustLinkMenuItem link={spanLink} />
        </Menu.Content>
      </Menu>

      {/* The menu items above pick the rating, so the popup is host-driven
          rather than trigger-driven. */}
      {feedback && (
        <MessageFeedbackPopover
          subject={feedback.subject}
          initialRating={feedback.initialRating}
          source="action"
          type={feedbackType}
          onClose={() => setFeedbackType(null)}
          anchor={anchorRef}
          onArchive={archive.isArchived ? undefined : archive.onSelect}
        />
      )}
    </div>
  )
}
